// src/pages/SetPasswordPage.tsx

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

/**
 * Öffentliche Seite für Passwort-Einrichtung via Setup-Token
 * 
 * Route: /passwort-setzen/:setupToken
 * Keine Authentifizierung erforderlich
 * 
 * Flow:
 * 1. User öffnet Link aus E-Mail
 * 2. Gibt neues Passwort ein (2x, Mindestlänge 6 Zeichen)
 * 3. Validierung via /.netlify/functions/secure-customer-set-password
 * 4. Bei Erfolg: Bestätigung + Link zum Dokumentenbereich
 * 
 * Wird von zwei Auslösern genutzt:
 * - XTAX sendet Passwort-Einladung im Intranet
 * - Kunde klickt "Passwort vergessen" auf /dokumente/:token
 */

export const SetPasswordPage: React.FC = () => {
  const { setupToken } = useParams<{ setupToken: string }>();
  const [passwort, setPasswort] = useState('');
  const [passwortConfirm, setPasswortConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    document.title = 'XTAX - Passwort festlegen';
    
    if (!setupToken) {
      setError('Ungültiger Link. Bitte überprüfen Sie die URL.');
    }
  }, [setupToken]);

  /**
   * Passwort-Validierung und Absenden
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!setupToken) {
      setError('Ungültiger Link.');
      return;
    }

    // Client-seitige Validierung
    if (!passwort.trim()) {
      setError('Bitte geben Sie ein Passwort ein.');
      return;
    }

    if (passwort.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }

    if (passwort !== passwortConfirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/.netlify/functions/secure-customer-set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          setup_token: setupToken,
          passwort,
          passwort_confirm: passwortConfirm
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setError(result.error || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
        setLoading(false);
        return;
      }

      // Erfolg
      setSuccess(true);
      setLoading(false);

    } catch (err) {
      console.error('Set password error:', err);
      setError('Ein technischer Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.');
      setLoading(false);
    }
  };

  // ============================================
  // SHARED: Security Badges
  // ============================================
  const SecurityBadges = () => (
    <div style={{
      display: 'flex',
      gap: '12px',
      justifyContent: 'center',
      marginBottom: '24px',
      flexWrap: 'wrap'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        color: '#374151',
        backgroundColor: '#f3f4f6',
        padding: '6px 12px',
        borderRadius: '4px',
        border: '1px solid #d1d5db'
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        256-Bit Verschlüsselung
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        color: '#374151',
        backgroundColor: '#f3f4f6',
        padding: '6px 12px',
        borderRadius: '4px',
        border: '1px solid #d1d5db'
      }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
          <path d="M9 12l2 2 4-4"/>
          <circle cx="12" cy="12" r="10"/>
        </svg>
        DSGVO-konform
      </div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        color: '#374151',
        backgroundColor: '#f3f4f6',
        padding: '6px 12px',
        borderRadius: '4px',
        border: '1px solid #d1d5db'
      }}>
        <span style={{ fontSize: '12px' }}>🇩🇪</span>
        Serverstandort: Frankfurt am Main
      </div>
    </div>
  );

  // ============================================
  // RENDER: Erfolg
  // ============================================
  if (success) {
    return (
      <div style={{
        backgroundColor: '#f9fafb',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px'
      }}>
        {/* Header */}
        <div style={{
          width: '100%',
          maxWidth: '500px',
          textAlign: 'center',
          marginBottom: '24px',
          marginTop: '40px'
        }}>
          <h1 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 8px 0'
          }}>
            Passwort festlegen
          </h1>
        </div>

        {/* Erfolg-Card */}
        <div style={{
          width: '100%',
          maxWidth: '500px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          padding: '32px',
          textAlign: 'center'
        }}>
          {/* Erfolg-Icon */}
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#ecfdf5',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px auto'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2">
              <path d="M9 12l2 2 4-4"/>
              <circle cx="12" cy="12" r="10"/>
            </svg>
          </div>

          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 12px 0'
          }}>
            Passwort erfolgreich gespeichert
          </h2>

          <p style={{
            fontSize: '14px',
            color: '#6b7280',
            margin: '0 0 24px 0',
            lineHeight: '1.5'
          }}>
            Sie können sich jetzt mit Ihrem neuen Passwort in Ihrem geschützten Dokumentenbereich anmelden.
          </p>

          <a
            href="https://www.xtax.de"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              backgroundColor: '#3465de',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              border: 'none',
              borderRadius: '8px',
              textDecoration: 'none',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2952b3'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3465de'}
          >
            Zur XTAX Website →
          </a>
        </div>

      </div>
    );
  }

  // ============================================
  // RENDER: Passwort-Formular
  // ============================================
  return (
    <div style={{
      backgroundColor: '#f9fafb',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px'
    }}>
      {/* Header */}
      <div style={{
        width: '100%',
        maxWidth: '500px',
        textAlign: 'center',
        marginBottom: '24px',
        marginTop: '40px'
      }}>
        <h1 style={{
          fontSize: '24px',
          fontWeight: '600',
          color: '#111827',
          margin: '0 0 8px 0'
        }}>
          Passwort festlegen
        </h1>
        <p style={{
          fontSize: '14px',
          color: '#6b7280',
          margin: '0'
        }}>
          Legen Sie Ihr persönliches Passwort für den geschützten Dokumentenbereich fest
        </p>
      </div>

      {/* Security Badges */}
      <SecurityBadges />

      {/* Hauptinhalt */}
      <div style={{
        width: '100%',
        maxWidth: '500px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        padding: '32px'
      }}>
        {/* Info-Box */}
        <div style={{
          backgroundColor: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3465de" strokeWidth="2" style={{ flexShrink: 0, marginTop: '1px' }}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <div>
            <p style={{
              margin: '0 0 4px 0',
              fontSize: '14px',
              fontWeight: '500',
              color: '#1e40af'
            }}>
              Hinweis
            </p>
            <p style={{
              margin: '0',
              fontSize: '13px',
              color: '#1e40af',
              lineHeight: '1.5'
            }}>
              Wählen Sie ein Passwort mit mindestens 6 Zeichen. Dieses Passwort benötigen Sie 
              künftig, um auf Ihre Dokumente im geschützten Bereich zuzugreifen.
            </p>
          </div>
        </div>

        {/* Formular */}
        <form onSubmit={handleSubmit}>
          {/* Passwort */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Neues Passwort
            </label>
            <input
              type="password"
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
              placeholder="Mindestens 6 Zeichen"
              disabled={loading || !setupToken}
              autoComplete="new-password"
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s, box-shadow 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3465de';
                e.target.style.boxShadow = '0 0 0 3px rgba(52, 101, 222, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Passwort bestätigen */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '8px'
            }}>
              Passwort bestätigen
            </label>
            <input
              type="password"
              value={passwortConfirm}
              onChange={(e) => setPasswortConfirm(e.target.value)}
              placeholder="Passwort erneut eingeben"
              disabled={loading || !setupToken}
              autoComplete="new-password"
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 0.2s, box-shadow 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3465de';
                e.target.style.boxShadow = '0 0 0 3px rgba(52, 101, 222, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          {/* Passwort-Stärke Indikator */}
          {passwort.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '13px',
                color: passwort.length >= 6 ? '#059669' : '#dc2626'
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  {passwort.length >= 6 ? (
                    <>
                      <path d="M9 12l2 2 4-4"/>
                      <circle cx="12" cy="12" r="10"/>
                    </>
                  ) : (
                    <>
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="12" y1="8" x2="12" y2="12"/>
                      <line x1="12" y1="16" x2="12.01" y2="16"/>
                    </>
                  )}
                </svg>
                {passwort.length >= 6 
                  ? `Mindestlänge erreicht (${passwort.length} Zeichen)` 
                  : `Noch ${6 - passwort.length} Zeichen erforderlich`
                }
              </div>
              {passwortConfirm.length > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '13px',
                  color: passwort === passwortConfirm ? '#059669' : '#dc2626',
                  marginTop: '6px'
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {passwort === passwortConfirm ? (
                      <>
                        <path d="M9 12l2 2 4-4"/>
                        <circle cx="12" cy="12" r="10"/>
                      </>
                    ) : (
                      <>
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="15" y1="9" x2="9" y2="15"/>
                        <line x1="9" y1="9" x2="15" y2="15"/>
                      </>
                    )}
                  </svg>
                  {passwort === passwortConfirm 
                    ? 'Passwörter stimmen überein' 
                    : 'Passwörter stimmen nicht überein'
                  }
                </div>
              )}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <p style={{
                margin: '0',
                fontSize: '14px',
                color: '#991b1b'
              }}>
                {error}
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !setupToken}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: loading ? '#9ca3af' : '#3465de',
              color: 'white',
              fontSize: '16px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => {
              if (!loading) e.currentTarget.style.backgroundColor = '#2952b3';
            }}
            onMouseLeave={(e) => {
              if (!loading) e.currentTarget.style.backgroundColor = '#3465de';
            }}
          >
            {loading ? (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                Wird gespeichert...
              </>
            ) : (
              <>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                Passwort speichern
              </>
            )}
          </button>
        </form>

        {/* Hilfe-Link */}
        <p style={{
          marginTop: '20px',
          textAlign: 'center',
          fontSize: '13px',
          color: '#6b7280'
        }}>
          Bei Fragen kontaktieren Sie uns unter{' '}
          <a href="mailto:lohn@xtax.de" style={{ color: '#3465de', textDecoration: 'none' }}>
            lohn@xtax.de
          </a>
        </p>
      </div>


      {/* Spinner Animation */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};
